import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import FilterBar from "./components/FilterBar";
import { works } from "@/mocks/works";
import type { Work } from "@/types";

const PAGE_SIZE = 8;

export default function WorksList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get("type") || "all";
  const sort = searchParams.get("sort") || "recommend";
  const page = Number(searchParams.get("page") || "1");

  const allWorks = works as Work[];

  const filtered = useMemo(() => {
    const list = allWorks.filter((work) => (type === "all" ? true : work.type === type));
    const sorted = [...list];
    if (sort === "date") {
      sorted.sort((a, b) => b.year - a.year);
    } else if (sort === "alpha") {
      sorted.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
    } else {
      sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    return sorted;
  }, [allWorks, type, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateParams = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === "") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    params.delete("page");
    setSearchParams(params, { replace: true });
  };

  const handleTypeChange = (value: string) => updateParams({ type: value === "all" ? null : value });
  const handleSortChange = (value: string) => updateParams({ sort: value === "recommend" ? null : value });
  const handlePageChange = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) {
      params.delete("page");
    } else {
      params.set("page", String(next));
    }
    setSearchParams(params, { replace: true });
    window.scrollTo({ top: 300, behavior: "smooth" });
  };
  const handleReset = () => setSearchParams({}, { replace: true });

  return (
    <div className="pt-16 md:pt-[72px]">
      <PageMeta
        title="佐山爱影视作品 - 电影与电视剧大全"
        description="佐山爱影视作品全集，涵盖电影、电视剧等多种类型，可按类型筛选、按时间与字母排序，快速找到你想看的作品。"
        keywords="佐山爱影视作品,佐山爱电影,佐山爱电视剧,影视作品列表,电影大全"
      />

      {/* 页头 */}
      <section className="px-5 md:px-10 pt-10 md:pt-14 pb-8 md:pb-10 bg-background-100">
        <div className="mb-5">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视作品" }]} />
        </div>
        <span className="text-xs font-medium tracking-[0.2em] uppercase text-accent-600">
          Works Collection
        </span>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mt-3 mb-4">
          佐山爱影视作品
        </h1>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
          这里收录了佐山爱出演的电影与电视剧作品档案，涵盖剧情、悬疑、科幻、治愈等多种题材。
          你可以按类型筛选，或调整排序方式，快速找到感兴趣的那一部。
        </p>
      </section>

      {/* 筛选与列表 */}
      <section className="px-5 md:px-10 py-10 md:py-14">
        <FilterBar
          type={type}
          sort={sort}
          onTypeChange={handleTypeChange}
          onSortChange={handleSortChange}
          resultCount={filtered.length}
        />

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <EmptyState
            icon="ri-film-line"
            title="暂无匹配作品"
            description="没有找到符合当前筛选条件的影视作品，试试切换类型或重置筛选条件。"
            actionLabel="重置筛选"
            onAction={handleReset}
          />
        )}
      </section>
    </div>
  );
}